import { Icon } from '@design/components/icon/Icon';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';

import styles from './Chip.module.scss';

export type ChipType = 'input' | 'choice' | 'filter';
export type ChipVariation = 'outline' | 'solid';

export interface ChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visible chip text. */
  label?: ReactNode;
  /** Active/selected state. Sets `aria-selected` and the active visual treatment. Defaults to `false`. */
  isActive?: boolean;
  /**
   * Behaviour preset:
   * `'input'` (default) for editable filter pills,
   * `'choice'` for tappable single-select options,
   * `'filter'` for active filters — replaces `rightIcon` with a Close (×) button.
   */
  type?: ChipType;
  /** Visual style: `'solid'` (default, filled) or `'outline'`. */
  variation?: ChipVariation;
  /** Optional icon rendered before the label. */
  leftIcon?: SvgIconNames;
  /** Optional icon rendered after the label. Ignored when `type === 'filter'` (replaced with the close icon). */
  rightIcon?: SvgIconNames;
  /** Size of the left icon in pixels. Defaults to `16`. */
  leftIconSize?: number;
  /** SVG viewBox forwarded to the left icon. */
  leftIconViewBox?: string;
  /** Size of the right icon in pixels. Defaults to `16`. */
  rightIconSize?: number;
  /** Optional logo/avatar element rendered between the left icon and the label. */
  assetLogo?: React.ReactNode;
  /** Arbitrary content rendered before everything else (before the left icon). */
  prefixNode?: React.ReactNode;
  /** Called when the chip body is clicked. */
  onClick?: (e: MouseEvent) => void;
  /** Called when the left icon is clicked (event bubbling will also trigger `onClick`). */
  onLeftIconClick?: () => void;
  /** Called when the right icon is clicked. For `type="filter"`, this is the close-button click. */
  onRightIconClick?: (e: MouseEvent) => void;
  /** Forwarded to the underlying `<button>`. */
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * Compact pill-shaped button for tags, filter values, single-select
 * choices, or inline editable inputs.
 *
 * Pick `type` based on the interaction model: `'input'` for editable
 * filter pills, `'choice'` for tappable options, `'filter'` for
 * already-applied filters with a built-in close button.
 *
 * Distinct from the read-only `Badge`: chips are interactive
 * `<button>`s with their own click handlers.
 */
export const Chip = ({
  label,
  isActive = false,
  disabled = false,
  type = 'input',
  variation = 'solid',
  leftIconSize = 16,
  leftIconViewBox,
  rightIconSize = 16,
  leftIcon,
  rightIcon,
  assetLogo,
  prefixNode,
  onLeftIconClick,
  onRightIconClick,
  onClick,
  className,
  ref,
  ...rest
}: ChipProps) => {
  return (
    <button
      ref={ref}
      data-chip
      data-testid="chip"
      aria-selected={isActive}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        styles.container,
        styles[type],
        styles[variation],
        className,
        { [styles.active]: isActive }
      )}
      {...rest}
    >
      {prefixNode}
      {leftIcon && (
        <span className={styles.iconContainer}>
          <Icon
            data-chip-left-icon
            className={styles.leftIcon}
            size={leftIconSize}
            iconName={leftIcon}
            viewBox={leftIconViewBox}
            onClick={onLeftIconClick}
          />
        </span>
      )}

      {assetLogo}
      {label && <span>{label}</span>}
      {rightIcon && type !== 'filter' && (
        <span className={styles.iconContainer}>
          <Icon
            data-chip-right-icon
            className={styles.rightIcon}
            size={rightIconSize}
            iconName={rightIcon}
            onClick={onRightIconClick}
          />
        </span>
      )}
      {type === 'filter' && (
        <span className={styles.iconContainer}>
          <Icon
            data-testid="chip-close-icon"
            data-chip-close-icon
            className={styles.closeIcon}
            iconName="Close"
            size={12}
            onClick={onRightIconClick}
          />
        </span>
      )}
    </button>
  );
};
